import type { Task } from '../../molecules/TaskList/TaskListMolecule.types';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export type TaskActionKind =
  | 'update'
  | 'catchup'
  | 'checkin-single'
  | 'none';

export type TaskActionVariant = 'primary' | 'secondary';

export interface TaskAction {
  kind: TaskActionKind;
  variant?: TaskActionVariant;
  label?: string;
  /** Missed-period count that drove the decision. Mirrors `getMissedCount(task)`. */
  missed: number;
}

/**
 * Number of missed periods for a task. Drives the action-button decision and the
 * streak-badge threshold. Workflow tasks always report `0` — "missed" is a
 * check-in concept. Collapsed check-in groups use the server-supplied `groupCount`
 * (which enumerates pending+overdue periods squashed into one row); single-period
 * rows count as `1` iff their status is `overdue`.
 */
export function getMissedCount(task: Task): number {
  if (task.taskType !== 'check_in') return 0;
  const grouped = task.groupCount ?? 0;
  if (grouped > 1) return grouped;
  return task.status === 'overdue' ? 1 : 0;
}

/**
 * Resolves the single context-aware action for a task row.
 *
 * Replaces the legacy two-button batch pattern ("Complete all" / "Skip older &
 * complete latest"). The dialog owns multi-period semantics now — the row only
 * opens the right mode.
 */
export function getActionForTask(task: Task, t: TranslateFn): TaskAction {
  const missed = getMissedCount(task);

  if (task.taskType === 'workflow') {
    return {
      kind: 'update',
      variant: 'secondary',
      label: t('taskList.actions.update'),
      missed,
    };
  }

  if (missed > 1) {
    return {
      kind: 'catchup',
      variant: 'primary',
      label: t('taskList.actions.catchUp', { count: missed }),
      missed,
    };
  }

  if (missed === 1) {
    return {
      kind: 'checkin-single',
      variant: 'primary',
      label: t('taskList.actions.checkIn'),
      missed,
    };
  }

  return { kind: 'none', missed };
}
